import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { usePartyFilter } from '../context/PartyFilterContext';
import { Layout } from '../components/Layout';
import { LoanFormFields } from '../components/LoanFormFields';
import { PartySelector } from '../components/PartySelector';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { formatEuro, formatPercent, formatDate, getExpiryStatus } from '../utils/formatters';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, CreditCard, Calendar, Building, AlertTriangle, Users, X, RefreshCw, Building2, User } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialLoanForm = {
  loan_name: '',
  lender_name: '',
  start_date: '',
  expiry_date: '',
  interest_renewal_date: '',
  linked_realestate_ids: [],
  borrower_ids: [],
  hms_ids: [],
  borg_ids: [],
  notes: '',
  // Hoofdsom
  initiele_hoofdsom: '',
  afgelost_bedrag: '',
  aflossingspercentage: '',
  // Rente structuur
  rente_type: 'effectief',
  euribor_termijn: '',
  euribor_waarde: '',
  opslag_rente_var: '',
  irs_referentie: '',
  irs_waarde: '',
  opslag_rente_vast: '',
  effectieve_rente: '',
  // Covenanten
  ltv_max: '',
  icr_min: '',
  dscr_min: '',
  debt_yield_min: '',
  rapportagedatum: '',
  meetfrequentie: 'jaar'
};

export const LoansPage = () => {
  const { canEdit, canDelete } = useAuth();
  const { t } = useLanguage();
  const { selectedPartyId } = usePartyFilter();
  const [loans, setLoans] = useState([]);
  const [properties, setProperties] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [form, setForm] = useState(initialLoanForm);
  const [saving, setSaving] = useState(false);
  const [euriborRates, setEuriborRates] = useState(null);
  const [fetchingEuribor, setFetchingEuribor] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedPartyId]);

  // Handler for when a new party is created inline
  const handlePartyCreated = (newParty) => {
    setParties(prev => [...prev, newParty]);
  };

  // Fetch Euribor rates from ECB
  const fetchEuriborRates = async () => {
    setFetchingEuribor(true);
    try {
      const response = await axios.get(`${API}/euribor/rates`, { withCredentials: true });
      if (response.data.success) {
        setEuriborRates(response.data.rates);
        toast.success(response.data.message);
        
        // Auto-fill the euribor value if a term is selected
        if (form.euribor_termijn && response.data.rates[form.euribor_termijn]) {
          setForm(prev => ({
            ...prev,
            euribor_waarde: response.data.rates[form.euribor_termijn].rate.toString()
          }));
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching Euribor rates:', error);
      toast.error('Kon Euribor tarieven niet ophalen');
    } finally {
      setFetchingEuribor(false);
    }
  };

  // Auto-fetch Euribor when term changes
  const handleEuriborTermChange = (term) => {
    setForm(prev => ({ ...prev, euribor_termijn: term }));
    if (euriborRates && euriborRates[term]) {
      setForm(prev => ({
        ...prev,
        euribor_termijn: term,
        euribor_waarde: euriborRates[term].rate.toString()
      }));
    }
  };

  const fetchData = async () => {
    try {
      const params = selectedPartyId !== 'all' ? `?party_id=${selectedPartyId}` : '';
      const [loansRes, propertiesRes, partiesRes] = await Promise.all([
        axios.get(`${API}/loans${params}`, { withCredentials: true }),
        axios.get(`${API}/realestate`, { withCredentials: true }),
        axios.get(`${API}/parties`, { withCredentials: true })
      ]);
      setLoans(loansRes.data);
      setProperties(propertiesRes.data);
      setParties(partiesRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (loan = null) => {
    if (loan) {
      setSelectedLoan(loan);
      setForm({
        loan_name: loan.loan_name,
        lender_name: loan.lender_name,
        start_date: loan.start_date || '',
        expiry_date: loan.expiry_date,
        interest_renewal_date: loan.interest_renewal_date,
        linked_realestate_ids: loan.linked_realestate_ids || [],
        borrower_ids: loan.borrower_ids || [],
        hms_ids: loan.hms_ids || [],
        borg_ids: loan.borg_ids || [],
        notes: loan.notes || '',
        // Hoofdsom
        initiele_hoofdsom: loan.initiele_hoofdsom || loan.loan_amount || '',
        afgelost_bedrag: loan.afgelost_bedrag || '',
        aflossingspercentage: loan.aflossingspercentage || loan.repayment_rate || '',
        // Rente structuur
        rente_type: loan.rente_type || 'effectief',
        euribor_termijn: loan.euribor_termijn || '',
        euribor_waarde: loan.euribor_waarde || '',
        opslag_rente_var: loan.opslag_rente_var || '',
        irs_referentie: loan.irs_referentie || '',
        irs_waarde: loan.irs_waarde || '',
        opslag_rente_vast: loan.opslag_rente_vast || '',
        effectieve_rente: loan.effectieve_rente || loan.interest_rate || '',
        // Covenanten
        ltv_max: loan.ltv_max || '',
        icr_min: loan.icr_min || '',
        dscr_min: loan.dscr_min || '',
        debt_yield_min: loan.debt_yield_min || '',
        rapportagedatum: loan.rapportagedatum || '',
        meetfrequentie: loan.meetfrequentie || 'jaar'
      });
    } else {
      setSelectedLoan(null);
      setForm(initialLoanForm);
    }
    setDialogOpen(true);
  };

  // Calculate effective interest rate based on rente_type
  const getEffectiveRate = () => {
    if (form.rente_type === 'variabel') {
      const euribor = parseFloat(form.euribor_waarde) || 0;
      const opslag = parseFloat(form.opslag_rente_var) || 0;
      return euribor + opslag;
    } else if (form.rente_type === 'vast') {
      const irs = parseFloat(form.irs_waarde) || 0;
      const opslag = parseFloat(form.opslag_rente_vast) || 0;
      return irs + opslag;
    } else {
      return parseFloat(form.effectieve_rente) || 0;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const effectiveRate = getEffectiveRate();
      const initHoofdsom = parseFloat(form.initiele_hoofdsom) || 0;
      const aflossPerc = parseFloat(form.aflossingspercentage) || 0;
      
      const payload = {
        ...form,
        // Map to backend fields
        loan_amount: initHoofdsom,  // initiële hoofdsom = loan_amount
        interest_rate: effectiveRate,  // calculated effective rate
        repayment_rate: aflossPerc,
        // Hoofdsom details
        initiele_hoofdsom: initHoofdsom,
        afgelost_bedrag: form.afgelost_bedrag ? parseFloat(form.afgelost_bedrag) : 0,
        aflossingspercentage: aflossPerc,
        // Rente structuur
        euribor_waarde: form.euribor_waarde ? parseFloat(form.euribor_waarde) : null,
        opslag_rente_var: form.opslag_rente_var ? parseFloat(form.opslag_rente_var) : null,
        irs_waarde: form.irs_waarde ? parseFloat(form.irs_waarde) : null,
        opslag_rente_vast: form.opslag_rente_vast ? parseFloat(form.opslag_rente_vast) : null,
        effectieve_rente: effectiveRate,
        // Covenanten
        ltv_max: form.ltv_max ? parseFloat(form.ltv_max) : null,
        icr_min: form.icr_min ? parseFloat(form.icr_min) : null,
        dscr_min: form.dscr_min ? parseFloat(form.dscr_min) : null,
        debt_yield_min: form.debt_yield_min ? parseFloat(form.debt_yield_min) : null
      };

      if (selectedLoan) {
        await axios.put(`${API}/loans/${selectedLoan.loan_id}`, payload, { withCredentials: true });
        toast.success(t('savedSuccessfully'));
      } else {
        await axios.post(`${API}/loans`, payload, { withCredentials: true });
        toast.success(t('savedSuccessfully'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/loans/${selectedLoan.loan_id}`, { withCredentials: true });
      toast.success(t('deletedSuccessfully'));
      setDeleteDialogOpen(false);
      setSelectedLoan(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getStatusBadge = (dateString) => {
    const status = getExpiryStatus(dateString);
    const styles = {
      expired: 'bg-red-500/20 text-red-400',
      warning: 'bg-amber-500/20 text-amber-400',
      caution: 'bg-yellow-500/20 text-yellow-400',
      ok: 'bg-emerald-500/20 text-emerald-400',
      neutral: 'bg-slate-500/20 text-slate-400'
    };
    return styles[status];
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64 bg-slate-800" />
          <Skeleton className="h-96 bg-slate-800" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="loans-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Comfortaa']">{t('loans')}</h1>
            <p className="text-slate-400 mt-1">
              {loans.length} {loans.length === 1 ? 'loan' : 'loans'} in portfolio
            </p>
          </div>
          <Button 
            className="bg-[#b4976f] hover:bg-[#a38663]"
            onClick={() => handleOpenDialog()}
            data-testid="add-loan-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('addLoan')}
          </Button>
        </div>

        {/* Loans Table */}
        <Card className="bg-[#1e293b] border-slate-700">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">{t('loanName')}</TableHead>
                  <TableHead className="text-slate-400">{t('lenderName')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('loanAmount')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('interestRate')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('interestAmount')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('repaymentAmount')}</TableHead>
                  <TableHead className="text-slate-400">{t('expiryDate')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('edit')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loans.length > 0 ? (
                  loans.map((loan) => (
                    <TableRow key={loan.loan_id} className="border-slate-700 table-row-hover">
                      <TableCell className="text-white font-medium">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-[#b4976f]" />
                          {loan.loan_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">{loan.lender_name}</TableCell>
                      <TableCell className="text-right text-[#c8b599] euro-amount">
                        {formatEuro(loan.loan_amount)}
                      </TableCell>
                      <TableCell className="text-right text-white">
                        {formatPercent(loan.interest_rate)}
                      </TableCell>
                      <TableCell className="text-right text-white euro-amount">
                        {formatEuro(loan.interest_amount)}
                      </TableCell>
                      <TableCell className="text-right text-white euro-amount">
                        {formatEuro(loan.repayment_amount)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(loan.expiry_date)}`}>
                          {formatDate(loan.expiry_date)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(loan)}
                            data-testid={`edit-loan-${loan.loan_id}`}
                          >
                            <Edit className="h-4 w-4 text-slate-400 hover:text-white" />
                          </Button>
                          {canDelete() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedLoan(loan);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`delete-loan-${loan.loan_id}`}
                            >
                              <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-400" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                      <CreditCard className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                      <p>{t('noData')}</p>
                      <Button 
                        variant="link" 
                        className="text-[#b4976f] mt-2"
                        onClick={() => handleOpenDialog()}
                      >
                        {t('addLoan')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#1e293b] border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Comfortaa']">
                {selectedLoan ? t('editLoan') : t('addLoan')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basis Informatie */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('loanName')}</Label>
                  <Input
                    value={form.loan_name}
                    onChange={(e) => setForm({ ...form, loan_name: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="loan-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('lenderName')}</Label>
                  <Input
                    value={form.lender_name}
                    onChange={(e) => setForm({ ...form, lender_name: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="lender-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Ingangsdatum</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('expiryDate')}</Label>
                  <Input
                    type="date"
                    value={form.expiry_date}
                    onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="expiry-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('interestRenewalDate')}</Label>
                  <Input
                    type="date"
                    value={form.interest_renewal_date}
                    onChange={(e) => setForm({ ...form, interest_renewal_date: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="renewal-date-input"
                  />
                </div>
              </div>
              
              {/* Borrowers Section - Using PartySelector Component */}
              <PartySelector
                parties={parties}
                selectedIds={form.borrower_ids}
                onSelectionChange={(ids) => setForm({ ...form, borrower_ids: ids })}
                onPartyCreated={handlePartyCreated}
                label={`${t('borrowers')} (Kredietnemers)`}
                placeholder={t('addBorrower')}
                colorScheme="emerald"
                icon={<Users className="h-4 w-4 text-[#b4976f]" />}
              />

              {/* HMS Section - Using PartySelector Component */}
              <PartySelector
                parties={parties}
                selectedIds={form.hms_ids}
                onSelectionChange={(ids) => setForm({ ...form, hms_ids: ids })}
                onPartyCreated={handlePartyCreated}
                partyTypeFilter="bv"
                label={`${t('hms')} (Hoofdelijk Medeschuldenaar - B.V.)`}
                placeholder={t('addHms')}
                colorScheme="amber"
                icon={<Building2 className="h-4 w-4 text-amber-400" />}
              />

              {/* Borg Section - Using PartySelector Component */}
              <PartySelector
                parties={parties}
                selectedIds={form.borg_ids}
                onSelectionChange={(ids) => setForm({ ...form, borg_ids: ids })}
                onPartyCreated={handlePartyCreated}
                partyTypeFilter="private"
                label={`${t('borg')} (Particuliere Borg)`}
                placeholder={t('addBorg')}
                colorScheme="blue"
                icon={<User className="h-4 w-4 text-blue-400" />}
              />

              {/* === HOOFDSOM & AFLOSSING SECTIE === */}
              <div className="space-y-3 p-4 rounded-lg bg-slate-800/50 border border-slate-600">
                <Label className="text-slate-300 font-medium text-base">Hoofdsom &amp; Aflossing</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">Initiële Hoofdsom (€) *</Label>
                    <Input
                      type="number"
                      value={form.initiele_hoofdsom}
                      onChange={(e) => setForm({ ...form, initiele_hoofdsom: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      step="0.01"
                      placeholder="Oorspronkelijk leningbedrag"
                      required
                      data-testid="loan-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">Aflossingspercentage (%) *</Label>
                    <Input
                      type="number"
                      value={form.aflossingspercentage}
                      onChange={(e) => setForm({ ...form, aflossingspercentage: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Jaarlijkse aflossing %"
                      required
                      data-testid="repayment-rate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">Cumulatief Afgelost (€)</Label>
                    <Input
                      type="number"
                      value={form.afgelost_bedrag}
                      onChange={(e) => setForm({ ...form, afgelost_bedrag: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      step="0.01"
                      placeholder="Tot nu toe afgelost"
                    />
                  </div>
                </div>
                {/* Calculated Pro Resto Hoofdsom - based on today's date */}
                <div className="mt-2 p-3 rounded bg-slate-700/50 flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Pro Resto Hoofdsom (per vandaag):</span>
                  <span className="text-[#b4976f] font-semibold text-lg">
                    {formatEuro((parseFloat(form.initiele_hoofdsom) || 0) - (parseFloat(form.afgelost_bedrag) || 0))}
                  </span>
                </div>
              </div>

              {/* === RENTE STRUCTUUR SECTIE === */}
              <div className="space-y-3 p-4 rounded-lg bg-slate-800/50 border border-slate-600">
                <Label className="text-slate-300 font-medium text-base">Rente Structuur</Label>
                
                <div className="space-y-2">
                  <Label className="text-slate-400 text-sm">Rente Type</Label>
                  <Select value={form.rente_type} onValueChange={(value) => setForm({ ...form, rente_type: value })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="effectief" className="text-white hover:bg-slate-800">Effectieve Rente (simpel)</SelectItem>
                      <SelectItem value="variabel" className="text-white hover:bg-slate-800">Variabel (Euribor + opslag)</SelectItem>
                      <SelectItem value="vast" className="text-white hover:bg-slate-800">Vast (IRS + opslag)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Variabele Rente velden */}
                {form.rente_type === 'variabel' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded bg-blue-900/20 border border-blue-800/50">
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">Euribor Termijn</Label>
                      <Select value={form.euribor_termijn} onValueChange={handleEuriborTermChange}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                          <SelectValue placeholder="Selecteer" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          <SelectItem value="1M" className="text-white hover:bg-slate-800">1 Maand</SelectItem>
                          <SelectItem value="3M" className="text-white hover:bg-slate-800">3 Maanden</SelectItem>
                          <SelectItem value="6M" className="text-white hover:bg-slate-800">6 Maanden</SelectItem>
                          <SelectItem value="12M" className="text-white hover:bg-slate-800">12 Maanden</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm flex items-center gap-2">
                        Euribor Waarde (%)
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={fetchEuriborRates}
                          disabled={fetchingEuribor}
                          className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300"
                        >
                          <RefreshCw className={`h-3 w-3 mr-1 ${fetchingEuribor ? 'animate-spin' : ''}`} />
                          {fetchingEuribor ? 'Laden...' : 'ECB Ophalen'}
                        </Button>
                      </Label>
                      <Input
                        type="number"
                        value={form.euribor_waarde}
                        onChange={(e) => setForm({ ...form, euribor_waarde: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                        step="0.001"
                        placeholder="bijv. 3.5"
                      />
                      {euriborRates && form.euribor_termijn && euriborRates[form.euribor_termijn] && (
                        <span className="text-xs text-slate-500">
                          ECB {euriborRates[form.euribor_termijn].date}: {euriborRates[form.euribor_termijn].rate}%
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">Opslag (%)</Label>
                      <Input
                        type="number"
                        value={form.opslag_rente_var}
                        onChange={(e) => setForm({ ...form, opslag_rente_var: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                        step="0.01"
                        placeholder="bijv. 1.5"
                      />
                    </div>
                    <div className="md:col-span-3 p-2 rounded bg-slate-700/50 flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Effectieve Rente (berekend):</span>
                      <span className="text-[#b4976f] font-semibold">
                        {((parseFloat(form.euribor_waarde) || 0) + (parseFloat(form.opslag_rente_var) || 0)).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                )}

                {/* Vaste Rente velden */}
                {form.rente_type === 'vast' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded bg-emerald-900/20 border border-emerald-800/50">
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">IRS Referentie</Label>
                      <Input
                        value={form.irs_referentie}
                        onChange={(e) => setForm({ ...form, irs_referentie: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                        placeholder="bijv. 5-jaars IRS"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">IRS Waarde (%)</Label>
                      <Input
                        type="number"
                        value={form.irs_waarde}
                        onChange={(e) => setForm({ ...form, irs_waarde: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                        step="0.001"
                        placeholder="bijv. 2.5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">Opslag (%)</Label>
                      <Input
                        type="number"
                        value={form.opslag_rente_vast}
                        onChange={(e) => setForm({ ...form, opslag_rente_vast: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                        step="0.01"
                        placeholder="bijv. 1.5"
                      />
                    </div>
                    <div className="md:col-span-3 p-2 rounded bg-slate-700/50 flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Effectieve Rente (berekend):</span>
                      <span className="text-[#b4976f] font-semibold">
                        {((parseFloat(form.irs_waarde) || 0) + (parseFloat(form.opslag_rente_vast) || 0)).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                )}

                {/* Effectieve Rente (simpel) */}
                {form.rente_type === 'effectief' && (
                  <div className="p-3 rounded bg-amber-900/20 border border-amber-800/50">
                    <div className="space-y-2">
                      <Label className="text-slate-400 text-sm">Effectieve Rente (%) *</Label>
                      <Input
                        type="number"
                        value={form.effectieve_rente}
                        onChange={(e) => setForm({ ...form, effectieve_rente: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white max-w-xs"
                        step="0.01"
                        placeholder="bijv. 4.5"
                        required
                        data-testid="interest-rate-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* === COVENANTEN SECTIE === */}
              <div className="space-y-3 p-4 rounded-lg bg-slate-800/50 border border-slate-600">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 font-medium text-base">Covenanten &amp; Ratio&apos;s</Label>
                  {selectedLoan?.covenant_status && (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      selectedLoan.covenant_status === 'overtreden' ? 'bg-red-500/20 text-red-400' :
                      selectedLoan.covenant_status === 'waarschuwingsniveau' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {selectedLoan.covenant_status === 'overtreden' ? 'Overtreden' :
                       selectedLoan.covenant_status === 'waarschuwingsniveau' ? 'Waarschuwing' : 'In Orde'}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* LTV */}
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">LTV Max (%)</Label>
                    <Input
                      type="number"
                      value={form.ltv_max}
                      onChange={(e) => setForm({ ...form, ltv_max: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="bijv. 75"
                    />
                    {selectedLoan?.ltv_huidig !== undefined && selectedLoan?.ltv_huidig !== null && (
                      <div className={`text-xs ${selectedLoan.ltv_max && selectedLoan.ltv_huidig > selectedLoan.ltv_max ? 'text-red-400' : 'text-slate-400'}`}>
                        Huidig: {selectedLoan.ltv_huidig}%
                      </div>
                    )}
                  </div>
                  
                  {/* ICR */}
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">ICR Min</Label>
                    <Input
                      type="number"
                      value={form.icr_min}
                      onChange={(e) => setForm({ ...form, icr_min: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      step="0.01"
                      placeholder="bijv. 2.0"
                    />
                    {selectedLoan?.icr_huidig !== undefined && selectedLoan?.icr_huidig !== null && (
                      <div className={`text-xs ${selectedLoan.icr_min && selectedLoan.icr_huidig < selectedLoan.icr_min ? 'text-red-400' : 'text-slate-400'}`}>
                        Huidig: {selectedLoan.icr_huidig}x
                      </div>
                    )}
                  </div>
                  
                  {/* DSCR */}
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">DSCR Min</Label>
                    <Input
                      type="number"
                      value={form.dscr_min}
                      onChange={(e) => setForm({ ...form, dscr_min: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      step="0.01"
                      placeholder="bijv. 1.2"
                    />
                    {selectedLoan?.dscr_huidig !== undefined && selectedLoan?.dscr_huidig !== null && (
                      <div className={`text-xs ${selectedLoan.dscr_min && selectedLoan.dscr_huidig < selectedLoan.dscr_min ? 'text-red-400' : 'text-slate-400'}`}>
                        Huidig: {selectedLoan.dscr_huidig}x
                      </div>
                    )}
                  </div>
                  
                  {/* Debt Yield */}
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">Debt Yield Min (%)</Label>
                    <Input
                      type="number"
                      value={form.debt_yield_min}
                      onChange={(e) => setForm({ ...form, debt_yield_min: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      step="0.1"
                      placeholder="bijv. 8"
                    />
                    {selectedLoan?.debt_yield_huidig !== undefined && selectedLoan?.debt_yield_huidig !== null && (
                      <div className={`text-xs ${selectedLoan.debt_yield_min && selectedLoan.debt_yield_huidig < selectedLoan.debt_yield_min ? 'text-red-400' : 'text-slate-400'}`}>
                        Huidig: {selectedLoan.debt_yield_huidig}%
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">Rapportagedatum</Label>
                    <Input
                      type="date"
                      value={form.rapportagedatum}
                      onChange={(e) => setForm({ ...form, rapportagedatum: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-sm">Meetfrequentie</Label>
                    <Select value={form.meetfrequentie} onValueChange={(value) => setForm({ ...form, meetfrequentie: value })}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="kwartaal" className="text-white hover:bg-slate-800">Kwartaal</SelectItem>
                        <SelectItem value="halfjaar" className="text-white hover:bg-slate-800">Halfjaar</SelectItem>
                        <SelectItem value="jaar" className="text-white hover:bg-slate-800">Jaar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t('notes')}</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  rows={3}
                  data-testid="loan-notes-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-slate-600 text-slate-300"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className="bg-[#b4976f] hover:bg-[#a38663]"
                  disabled={saving}
                  data-testid="save-loan-btn"
                >
                  {saving ? t('loading') : t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-[#1e293b] border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                {t('confirmDelete')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                This will permanently delete the loan &quot;{selectedLoan?.loan_name}&quot;.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-600 text-slate-300 hover:bg-slate-800">
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
